import type { NonUndefined } from 'utility-types'

import { assert, type ReadonlyNonEmptyArray } from '../../../../../utils'

import { ConnectwareError, ConnectwareErrorType, type PaginatedData, type StatusType } from '../../../../../domain'

import type { PageSubscriptionsTypes, SingleSubscriptionsTypes, SubscriptionFilterArgs, SubscriptionPageOptions } from '../../../../../application'

import type {
    BackendHttpMethod,
    BackendQueryParameters,
    ServiceDeviationsSupportedType,
    ServicesDeviationsSupportedType,
    StatusSupportedType,
} from '../../../../Connectware'
import type { HttpRequestArgs } from '../../..'
import { isResource, mapSearch, type Resource, type ResourceCategories } from '../../../mappers'
import { AsyncMapper, createSortMapper, mapPagination, mapPaginationQueryParameters, type SortableColumnsMap } from '../../mappers'
import { BaseSubscriptionStrategy } from './Base'

export type SingleResourceBaseRequest<C extends ResourceCategories> = Pick<
    HttpRequestArgs<'/api/v2/resources/+', 'GET', Resource<C>>,
    'capability' | 'path' | 'method'
>

/**
 * This is a helper base class to assist interactions with the resources endpoints
 * in order to load a single resource.
 */
export abstract class BaseSingleResourceSubscriptionStrategy<
    C extends ResourceCategories,
    S extends keyof SingleSubscriptionsTypes
> extends BaseSubscriptionStrategy<never, S> {
    /**
     * The expected category
     */
    protected abstract readonly resourceCategory: C

    /**
     * Used when resource cannot be found
     */
    protected abstract readonly notFoundMessage: string

    /**
     * Used to fetch status with RST
     */
    protected abstract readonly resourceStatusType: StatusSupportedType

    /**
     * The base request to reach the endpoint
     * Is split like so in order to allow endpoints usage to be tracked to specific capabilities
     */
    protected abstract readonly baseRequest: SingleResourceBaseRequest<C>

    /**
     * Will be called once all values have been yielded as expected
     */
    protected abstract mapResource (resource: Resource<C>, status: StatusType): SingleSubscriptionsTypes[S]

    override async retrieveOne (id: string): Promise<SingleSubscriptionsTypes[S]> {
        return Promise.all([
            this.request<'/api/v2/resources/+', 'GET', Resource<C>>({
                ...this.baseRequest,
                pathParams: [id],
                authenticate: true,
                handlers: {
                    200: (r) =>
                        r.getJson().then(({ data }) => {
                            assert(
                                isResource(data, this.resourceCategory),
                                new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Unexpected resource category', {
                                    expected: this.resourceCategory,
                                    actual: data.category,
                                })
                            )
                            return data
                        }),
                    404: BaseSingleResourceSubscriptionStrategy.createNotFoundErrorHandler('/api/v2/resources/+', id),
                    500: BaseSingleResourceSubscriptionStrategy.createServerErrorHandler(this.notFoundMessage),
                },
            }),
            this.rstAdapter.fetchStatusType(this.resourceStatusType, id),
        ]).then(([resource, status]) => this.mapResource(resource, status))
    }

    createSingleChangeListener (id: string, handler: VoidFunction): Promise<VoidFunction> {
        return this.rstAdapter.subscribeToStatusType(this.resourceStatusType, id, handler)
    }
}

class ResourceMapper<C extends ResourceCategories, P extends keyof PageSubscriptionsTypes, A> extends AsyncMapper<Resource<C>, A, PageSubscriptionsTypes[P]> {
    constructor (
        protected readonly mapResource: (resource: Resource<C>, aux: A) => PageSubscriptionsTypes[P],
        ...args: ConstructorParameters<typeof AsyncMapper<Resource, A, PageSubscriptionsTypes[P]>>
    ) {
        super(...args)
    }

    protected map (resource: Resource<C>): Promise<PageSubscriptionsTypes[P]> {
        return this.fetchAux(resource.resourceId).then((aux) => this.mapResource(resource, aux))
    }
}

type SupportedResourcePaths = '/api/v2/services/+/resources' | '/api/v2/resources'

export type PageResourceQueryParameters = BackendQueryParameters<SupportedResourcePaths, 'GET'>
export type PageResourceSortableColumnsMap<P extends keyof PageSubscriptionsTypes> =
    /** Force a relationship between the subscription type and the orderBy column */
    SortableColumnsMap<PageSubscriptionsTypes[P], NonUndefined<PageResourceQueryParameters['orderBy']>>

type PickPageResourcesRequestConfig<
    E extends SupportedResourcePaths,
    P extends keyof PageSubscriptionsTypes,
    K extends keyof HttpRequestArgs<E, BackendHttpMethod<E>, unknown>
> = Pick<HttpRequestArgs<E, BackendHttpMethod<E>, PaginatedData<PageSubscriptionsTypes[P]>>, K>

type PageResourcesRequestConfig<E extends SupportedResourcePaths, P extends keyof PageSubscriptionsTypes> =
    /** Pick the parts necessary for the capabilities script to run */
    PickPageResourcesRequestConfig<E, P, 'capability' | 'method' | 'path'>

export type ServiceResourcesRequestConfig<P extends keyof PageSubscriptionsTypes> = PageResourcesRequestConfig<'/api/v2/services/+/resources', P>
export type AllResourcesRequestConfig<P extends keyof PageSubscriptionsTypes> = PageResourcesRequestConfig<'/api/v2/resources', P>

export abstract class BasePageResourceSubscriptionStrategy<
    C extends ResourceCategories,
    P extends keyof PageSubscriptionsTypes,
    A
> extends BaseSubscriptionStrategy<P, never> {
    /**
     * Whenever resources are filtered by a service, this will be called
     */
    protected abstract readonly serviceResourcesRequestConfig: ServiceResourcesRequestConfig<P>

    /**
     * Whenever resources are not filtered by a service, this will be called
     */
    protected abstract readonly allResourcesRequestConfig: AllResourcesRequestConfig<P>

    /**
     * The categories used as filter when fetching backend data
     */
    protected abstract readonly resourceCategories: ReadonlyNonEmptyArray<C>

    /**
     * If there is a 500 from the backend, this message will be used on the outbound error
     */
    protected abstract readonly serverErrorMessage: string

    /**
     * Map from domain to backend orderBy columns
     */
    protected abstract sortingColumns: PageResourceSortableColumnsMap<P>

    override readonly supportsPagination: BaseSubscriptionStrategy<P, never>['supportsPagination'] = true

    /**
     * Will be called for each resource being yielded
     */
    protected abstract mapResource (resource: Resource<C>, aux: A): PageSubscriptionsTypes[P]

    /**
     * Will be called to fetch necessary additional information for each resource
     */
    protected abstract fetchAux (resourceId: Resource<C>['resourceId']): Promise<A>

    /**
     * Certain strategies may want to have extra parameters in order to filter the response even more
     */
    protected createCustomQueryParameters? (filter: SubscriptionFilterArgs): PageResourceQueryParameters

    override retrievePage ({
        search,
        sort,
        pagination,
        ...filter
    }: SubscriptionFilterArgs & SubscriptionPageOptions<P>): Promise<PaginatedData<PageSubscriptionsTypes[P]>> {
        const mapSort = createSortMapper(this.sortingColumns)

        const args: PickPageResourcesRequestConfig<SupportedResourcePaths, P, 'authenticate' | 'queryParams' | 'handlers'> = {
            authenticate: true,
            queryParams: {
                categories: this.resourceCategories,
                ...mapPaginationQueryParameters(pagination),
                ...mapSort(sort),
                ...this.createCustomQueryParameters?.(filter),
                ...mapSearch(search),
                /**
                 * Only enabled resources are wanted
                 * Disabled resources should be ignored by the UI
                 */
                onlyFromEnabledServices: 'true',
            },
            handlers: {
                200: async (response) => {
                    const { data, meta } = await response.getJson()

                    const mapper = new ResourceMapper<C, P, A>(
                        (resource, aux) => this.mapResource(resource, aux),
                        (resourceId) => this.fetchAux(resourceId)
                    )

                    for (const resource of data) {
                        assert(
                            isResource(resource, ...this.resourceCategories),
                            new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Unexpected resource category', {
                                category: resource.category,
                                expected: this.resourceCategories,
                            })
                        )
                        mapper.push(resource)
                    }

                    return { current: await mapper.values, ...mapPagination(meta.pagination) }
                },
                400: BasePageResourceSubscriptionStrategy.createBadRequestHandler('fetching resources'),
                500: BasePageResourceSubscriptionStrategy.createServerErrorHandler(this.serverErrorMessage),
            },
        } as const

        return filter.service
            ? this.request({ ...this.serviceResourcesRequestConfig, pathParams: [filter.service], ...args })
            : this.request({ ...this.allResourcesRequestConfig, ...args })
    }
}

export abstract class BaseStatefulPageResourceSubscriptionStrategy<
    C extends ResourceCategories,
    P extends keyof PageSubscriptionsTypes
> extends BasePageResourceSubscriptionStrategy<C, P, StatusType> {
    /**
     * The type used to fetch RST statuses
     */
    protected abstract readonly resourceStatusType: StatusSupportedType

    /**
     * The type used to subscribe to general service deviations
     */
    protected abstract readonly serviceDeviationsType: readonly [allServices: ServicesDeviationsSupportedType, specificService: ServiceDeviationsSupportedType]

    protected override fetchAux (resourceId: Resource<C>['resourceId']): Promise<StatusType> {
        return this.rstAdapter.fetchStatusType(this.resourceStatusType, resourceId)
    }

    createAllChangeListener ({ service }: SubscriptionFilterArgs, handler: VoidFunction): Promise<VoidFunction> {
        const [allServices, specificService] = this.serviceDeviationsType

        if (!service) {
            /** Subscribe to deviations across all services */
            return this.rstAdapter.subscribeToServicesDeviationsCount(allServices, handler)
        }

        /** Subscribe to deviations on the service used as filter for this listing of resources */
        return this.rstAdapter.subscribeToServiceDeviationsCount(specificService, service, handler)
    }
}
